import { useEffect, useRef, useState } from 'react';

interface UseSyncedFieldValueParams {
  savedValue: string;
  rejectedSaves: number;
  isSaving: boolean;
}

const useSyncedFieldValue = ({
  savedValue,
  rejectedSaves,
  isSaving,
}: UseSyncedFieldValueParams) => {
  const [value, setValue] = useState(savedValue);
  const isFocusedRef = useRef(false);
  const hasPendingSyncRef = useRef(false);
  const appliedValueRef = useRef(savedValue);
  const appliedRejectedSavesRef = useRef(rejectedSaves);

  useEffect(() => {
    const hasNewSavedValue = savedValue !== appliedValueRef.current;
    const wasSaveRejected = rejectedSaves !== appliedRejectedSavesRef.current;

    if (!hasNewSavedValue && !wasSaveRejected) {
      return;
    }

    if (isFocusedRef.current || isSaving) {
      hasPendingSyncRef.current = true;
      return;
    }

    appliedValueRef.current = savedValue;
    appliedRejectedSavesRef.current = rejectedSaves;
    hasPendingSyncRef.current = false;
    setValue(savedValue);
  }, [savedValue, rejectedSaves, isSaving]);

  const onFocus = () => {
    isFocusedRef.current = true;
  };

  const onBlur = (): boolean => {
    isFocusedRef.current = false;

    if (!hasPendingSyncRef.current) {
      return false;
    }

    appliedValueRef.current = savedValue;
    appliedRejectedSavesRef.current = rejectedSaves;
    hasPendingSyncRef.current = false;
    setValue(savedValue);

    return true;
  };

  return { value, setValue, onFocus, onBlur };
};

export default useSyncedFieldValue;
